import { useEffect, useRef, useState } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import appSourcePlanCopy from '@/i18n/ns/app-source-plan';
import { SessionChangedError } from '@/lib/session-guard';
import { Button } from '@/components/ui/button';
import { AppSourcesUnavailable, type AppSourcesApi } from './app-sources-api';
import { projectPreview, type PreviewView } from './preview-model';

type State =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'ready'; view: PreviewView }
  | { phase: 'refused'; reason: 'denied' | 'changed' | 'unavailable'; retry: boolean };

/**
 * A bounded read of what ONE declared source/tool returns for the selected saved version, drawn as it came.
 * It is a read: plain retry, no operation key. A reply for another selection or an older request is dropped;
 * a replaced session shows nothing and retries nothing. It states no count, label or coverage of its own.
 */
export function SourcePreview({
  api,
  sourceId,
  toolId,
}: {
  api: Pick<AppSourcesApi, 'preview'>;
  sourceId: string;
  toolId: string;
}) {
  const copy = useNamespaceCopy(appSourcePlanCopy);
  const [state, setState] = useState<State>({ phase: 'idle' });
  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      generation.current++;
      controller.current?.abort();
    };
  }, []);
  const load = async () => {
    const mine = ++generation.current;
    controller.current?.abort();
    controller.current = new AbortController();
    setState({ phase: 'loading' });
    try {
      const result = await api.preview({ sourceId, toolId }, { signal: controller.current.signal });
      if (!alive.current || mine !== generation.current) return;
      if (result.ok) return setState({ phase: 'ready', view: projectPreview(result.data) });
      const denied = /NOT_AUTHORIZED|DENIED|FORBIDDEN/iu.test(result.code);
      setState({
        phase: 'refused',
        reason: denied ? 'denied' : 'unavailable',
        retry: !denied && result.retryable,
      });
    } catch (error) {
      if (error instanceof SessionChangedError) return;
      if (!alive.current || mine !== generation.current) return;
      const status = error instanceof AppSourcesUnavailable ? error.status : null;
      setState({
        phase: 'refused',
        reason: status === 403 ? 'denied' : status === 409 ? 'changed' : 'unavailable',
        retry: status !== 403,
      });
    }
  };
  const hide = () => {
    generation.current++;
    controller.current?.abort();
    setState({ phase: 'idle' });
  };
  return (
    <div className="pc-source-preview" data-testid="source-preview">
      {state.phase === 'idle' ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="source-preview-open"
          onClick={() => void load()}
        >
          {copy.preview}
        </Button>
      ) : (
        <Button type="button" variant="ghost" size="sm" onClick={hide}>
          {copy.previewHide}
        </Button>
      )}
      {state.phase === 'loading' && (
        <p role="status" aria-busy="true">
          {copy.previewing}
        </p>
      )}
      {state.phase === 'refused' && (
        <div role="alert" data-testid="source-preview-refused">
          <p>
            {state.reason === 'denied'
              ? copy.previewDenied
              : state.reason === 'changed'
                ? copy.previewChanged
                : copy.previewUnavailable}
          </p>
          {state.retry && (
            <Button type="button" variant="outline" size="sm" onClick={() => void load()}>
              {copy.previewRetry}
            </Button>
          )}
        </div>
      )}
      {state.phase === 'ready' && <PreviewBody view={state.view} />}
    </div>
  );
}

function PreviewBody({ view }: { view: PreviewView }) {
  const copy = useNamespaceCopy(appSourcePlanCopy);
  if (view.kind === 'empty') return <p data-testid="source-preview-empty">{copy.previewEmpty}</p>;
  if (view.kind === 'json')
    return (
      <pre data-testid="source-preview-json" className="pc-data-muted">
        {view.text}
      </pre>
    );
  if (view.kind === 'text') return <p data-testid="source-preview-text">{view.text}</p>;
  if (view.kind === 'facts')
    return (
      <dl data-testid="source-preview-facts">
        {view.entries.map(([key, value]) => (
          <div key={key}>
            <dt>{key}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    );
  return (
    <div data-testid="source-preview-table">
      <table>
        <thead>
          <tr>
            {view.columns.map((column) => (
              <th key={column} scope="col">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {view.rows.map((row, index) => (
            <tr key={index} data-testid="source-preview-row">
              {row.map((cell, at) => (
                <td key={at}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {view.more && <p className="pc-data-muted">{copy.previewMore}</p>}
    </div>
  );
}
